import Icon, { DownloadOutlined, MoreOutlined } from "@ant-design/icons";
import IconStatusbarDownsampling from "@images/icons/icon-statusbar-downsampling.svg?react";
import IconStatusbarMouseLeft from "@images/icons/icon-statusbar-mouse-left.svg?react";
import IconStatusbarMouseLeftDrag from "@images/icons/icon-statusbar-mouse-left-drag.svg?react";
import IconStatusbarMouseRight from "@images/icons/icon-statusbar-mouse-right.svg?react";
import IconStatusbarMouseRightDrag from "@images/icons/icon-statusbar-mouse-right-drag.svg?react";
import IconStatusbarMouseWheel from "@images/icons/icon-statusbar-mouse-wheel.svg?react";
import { Space, Typography } from "antd";
import FastTooltip from "components/fast_tooltip";
import { formatCountToDataAmountUnit } from "libs/format_utils";
import { V3 } from "libs/mjs";
import { useInterval } from "libs/react_helpers";
import { useKeyPress, useWkSelector } from "libs/react_hooks";
import messages from "messages";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import type { AdditionalCoordinate } from "types/api_types";
import type { Vector3 } from "viewer/constants";
import { AltOrOptionKey, MappingStatusEnum, OrthoViews } from "viewer/constants";
import {
  type ActionDescriptor,
  getToolControllerForAnnotationTool,
} from "viewer/controller/combinations/tool_controls";
import {
  getMappingInfoOrNull,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import { getActiveMagInfo } from "viewer/model/accessors/flycam_accessor";
import { AnnotationTool, adaptActiveToolToShortcuts } from "viewer/model/accessors/tool_accessor";
import {
  getGlobalMousePosition,
  isPlaneMode as getIsPlaneMode,
} from "viewer/model/accessors/view_mode_accessor";
import {
  getActiveSegmentationTracing,
  getReadableNameForLayerName,
  getSegmentationLayerForTracing,
} from "viewer/model/accessors/volumetracing_accessor";
import {
  setActiveNodeAction,
  setActiveTreeAction,
} from "viewer/model/actions/skeletontracing_actions";
import { setActiveCellAction } from "viewer/model/actions/volumetracing_actions";
import { getSegmentIdRangeForElementClass } from "viewer/model/bucket_data_handling/data_rendering_logic";
import { getGlobalDataConnectionInfo } from "viewer/model/data_connection_info";
import { Store } from "viewer/singletons";
import BorderToggleButton from "./components/border_toggle_button";
import { NumberInputPopoverSetting } from "./left_border_tabs/components/number_input_popover_setting";

const { Text } = Typography;

const lineColor = "rgba(255, 255, 255, 0.67)";
const moreIconStyle = {
  height: 14,
  color: lineColor,
};
const moreLinkStyle = {
  marginLeft: 25,
  marginRight: "auto",
};

function getPosString(
  pos: Vector3,
  optAdditionalCoordinates: AdditionalCoordinate[] | null | undefined,
) {
  const additionalCoordinates = (optAdditionalCoordinates || []).map((coord) => coord.value);
  return V3.floor(pos).concat(additionalCoordinates).join(",");
}

function ZoomShortcut() {
  return (
    <span key="zoom" className="shortcut-info-element">
      <Text keyboard>{AltOrOptionKey}</Text>
      +
      <Icon component={IconStatusbarMouseWheel} aria-label="Mouse Wheel" /> Zoom in/out
    </span>
  );
}

function LeftClickShortcut({ actionDescriptor }: { actionDescriptor: ActionDescriptor }) {
  const leftClick =
    actionDescriptor.leftClick != null ? (
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseLeft} aria-label="Mouse Left Click" />
        {actionDescriptor.leftClick}
      </Space>
    ) : null;
  const leftDrag =
    actionDescriptor.leftDrag != null ? (
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseLeftDrag} aria-label="Mouse Left Drag" />
        {actionDescriptor.leftDrag}
      </Space>
    ) : null;
  return (
    <span>
      {leftClick}
      {leftDrag}
    </span>
  );
}

function RightClickShortcut({ actionDescriptor }: { actionDescriptor: ActionDescriptor }) {
  const rightClick =
    actionDescriptor.rightClick != null ? (
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseRight} aria-label="Mouse Right Click" />
        {actionDescriptor.rightClick}
      </Space>
    ) : null;
  const rightDrag =
    actionDescriptor.rightDrag != null ? (
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseRightDrag} aria-label="Mouse Right Drag" />
        {actionDescriptor.rightDrag}
      </Space>
    ) : null;
  return (
    <React.Fragment>
      {rightClick}
      {rightDrag}
    </React.Fragment>
  );
}

const getMoreShortcutsInfo = () => {
  return (
    <>
      <div style={{ marginLeft: 25 }}>
        <Text keyboard>Ctrl + P</Text> Commands
      </div>
      {moreShortcutsLink}
    </>
  );
};

const moreShortcutsLink = (
  <a
    target="_blank"
    href="https://docs.webknossos.org/webknossos/ui/keyboard_shortcuts.html"
    rel="noopener noreferrer"
    style={moreLinkStyle}
  >
    <FastTooltip title="More Shortcuts">
      <MoreOutlined rotate={90} style={moreIconStyle} />
    </FastTooltip>
  </a>
);

function ShortcutsInfo() {
  const activeTool = useWkSelector((state) => state.uiInformation.activeTool);
  const userConfiguration = useWkSelector((state) => state.userConfiguration);
  const isPlaneMode = useWkSelector((state) => getIsPlaneMode(state));
  const isShiftPressed = useKeyPress("Shift");
  const isControlOrMetaPressed = useKeyPress("ControlOrMeta");
  const isAltPressed = useKeyPress("Alt");
  const hasSkeleton = useWkSelector((state) => state.annotation.skeleton != null);
  const isTDViewportActive = useWkSelector(
    (state) => state.viewModeData.plane.activeViewport === OrthoViews.TDView,
  );

  if (!isPlaneMode) {
    let actionDescriptor = null;
    if (hasSkeleton && isShiftPressed) {
      actionDescriptor = getToolControllerForAnnotationTool(
        AnnotationTool.SKELETON,
      ).getActionDescriptors(
        AnnotationTool.SKELETON,
        userConfiguration,
        isShiftPressed,
        isControlOrMetaPressed,
        isAltPressed,
        isTDViewportActive,
      );
    }

    return (
      <React.Fragment>
        {actionDescriptor != null ? (
          <LeftClickShortcut actionDescriptor={actionDescriptor} />
        ) : (
          <span className="shortcut-info-element">
            <Icon component={IconStatusbarMouseLeftDrag} aria-label="Mouse Left Drag" />
            Move
          </span>
        )}
        <Space size="small" className="shortcut-info-element">
          <Text keyboard>Space</Text>
          Trace forward
        </Space>
        <Space size="small" className="shortcut-info-element">
          <Text keyboard>Ctrl + Space</Text>
          Trace backward
        </Space>
        <Space size="small" className="shortcut-info-element">
          <Text keyboard>◀ / ▶</Text>
          Rotation
        </Space>
        {getMoreShortcutsInfo()}
      </React.Fragment>
    );
  }

  const adaptedTool = adaptActiveToolToShortcuts(
    activeTool,
    isShiftPressed,
    isControlOrMetaPressed,
    isAltPressed,
  );
  const toolController = getToolControllerForAnnotationTool(adaptedTool);
  const actionDescriptor = toolController.getActionDescriptors(
    adaptedTool,
    userConfiguration,
    isShiftPressed,
    isControlOrMetaPressed,
    isAltPressed,
    isTDViewportActive,
  );

  return (
    <React.Fragment>
      <LeftClickShortcut actionDescriptor={actionDescriptor} />
      <RightClickShortcut actionDescriptor={actionDescriptor} />
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseWheel} aria-label="Mouse Wheel" />
        {isAltPressed || isControlOrMetaPressed ? "Zoom in/out" : "Move along 3rd axis"}
      </Space>
      <Space size="small" className="shortcut-info-element">
        <Icon component={IconStatusbarMouseRightDrag} aria-label="Mouse Right" />
        Rotate 3D View
      </Space>
      <ZoomShortcut />
      {getMoreShortcutsInfo()}
    </React.Fragment>
  );
}

function SegmentInfo() {
  const visibleSegmentationLayer = useWkSelector((state) => getVisibleSegmentationLayer(state));
  const hasVisibleSegmentation = visibleSegmentationLayer != null;
  const activeMappingInfo = useWkSelector((state) =>
    getMappingInfoOrNull(
      state.temporaryConfiguration.activeMappingByLayer,
      visibleSegmentationLayer?.name,
    ),
  );
  const hoveredSegmentId = useWkSelector((state) => state.temporaryConfiguration.hoveredSegmentId);

  if (hasVisibleSegmentation == null) {
    return null;
  }

  const idString =
    hoveredSegmentId == null
      ? "-"
      : activeMappingInfo?.mappingStatus === MappingStatusEnum.ENABLED
        ? `${hoveredSegmentId} (mapped)`
        : `${hoveredSegmentId}`;

  return <span className="info-element">Segment {idString}</span>;
}

function Infos() {
  const isSkeletonAnnotation = useWkSelector((state) => state.annotation.skeleton != null);
  const activeVolumeTracing = useWkSelector((state) => getActiveSegmentationTracing(state));

  const activeCellId = activeVolumeTracing?.activeCellId;
  const activeNodeId = useWkSelector((state) =>
    state.annotation.skeleton ? state.annotation.skeleton.activeNodeId : null,
  );
  const activeTreeId = useWkSelector((state) =>
    state.annotation.skeleton ? state.localSkeletonState.activeTreeId : null,
  );
  const dispatch = useDispatch();

  const onChangeActiveCellId = useCallback(
    (id: bigint) => dispatch(setActiveCellAction(id)),
    [dispatch],
  );
  const onChangeActiveNodeId = useCallback(
    (id: number) => {
      dispatch(setActiveNodeAction(id));
    },
    [dispatch],
  );
  const onChangeActiveTreeId = useCallback(
    (id: number) => dispatch(setActiveTreeAction(id)),
    [dispatch],
  );

  const validSegmentIdRange = useWkSelector((state) => {
    if (!activeVolumeTracing) {
      return null;
    }
    const segmentationLayer = getSegmentationLayerForTracing(state, activeVolumeTracing);
    const elementClass = segmentationLayer.elementClass;
    return getSegmentIdRangeForElementClass(elementClass);
  });

  return (
    <React.Fragment>
      <SegmentAndMousePosition />
      <span className="info-element">
        <DownloadSpeedometer />
      </span>
      {activeVolumeTracing != null && validSegmentIdRange != null ? (
        <span className="info-element">
          <NumberInputPopoverSetting
            value={activeCellId ?? null}
            label="Active Segment"
            min={validSegmentIdRange[0]}
            max={validSegmentIdRange[1]}
            detailedLabel="Change Active Segment ID"
            onChange={onChangeActiveCellId}
          />
        </span>
      ) : null}
      {isSkeletonAnnotation ? (
        <span className="info-element">
          <NumberInputPopoverSetting
            value={activeNodeId}
            label="Active Node"
            detailedLabel="Change Active Node ID"
            onChange={onChangeActiveNodeId}
          />
        </span>
      ) : null}
      {isSkeletonAnnotation ? (
        <span className="info-element">
          <NumberInputPopoverSetting
            value={activeTreeId}
            label="Active Tree"
            detailedLabel="Change Active Tree ID"
            onChange={onChangeActiveTreeId}
          />
        </span>
      ) : null}
      <MagnificationInfo />
    </React.Fragment>
  );
}

function DownloadSpeedometer() {
  const [currentBucketDownloadSpeed, setCurrentBucketDownloadSpeed] = useState<number>(0);
  const [totalDownloadedByteCount, setTotalDownloadedByteCount] = useState<number>(0);
  useInterval(() => {
    const { avgDownloadSpeedInBytesPerS, accumulatedDownloadedBytes } =
      getGlobalDataConnectionInfo().getStatistics();
    setCurrentBucketDownloadSpeed(avgDownloadSpeedInBytesPerS);
    setTotalDownloadedByteCount(accumulatedDownloadedBytes);
  }, 1500);

  return (
    <FastTooltip
      title={`Downloaded ${formatCountToDataAmountUnit(
        totalDownloadedByteCount,
      )} of Image Data (after decompression)`}
    >
      <DownloadOutlined className="icon-margin-right" />
      {formatCountToDataAmountUnit(currentBucketDownloadSpeed)}/s
    </FastTooltip>
  );
}

function MagnificationInfo() {
  const { representativeMag, isActiveMagGlobal } = useWkSelector(getActiveMagInfo);

  const renderMagTooltipContent = useCallback(() => {
    const state = Store.getState();
    const { activeMagOfEnabledLayers } = getActiveMagInfo(state);
    const dataset = state.dataset;
    const annotation = state.annotation;

    return (
      <div style={{ width: 200 }}>
        Rendered magnification per layer:
        <ul>
          {Object.entries(activeMagOfEnabledLayers).map(([layerName, mag]) => {
            const readableName = getReadableNameForLayerName(dataset, annotation, layerName);

            return (
              <li key={layerName}>
                {readableName}: {mag ? mag.join("-") : "none"}
              </li>
            );
          })}
        </ul>
        {messages["dataset.mag_explanation"]}
      </div>
    );
  }, []);

  if (representativeMag == null) {
    return null;
  }

  return (
    <span className="info-element">
      <Icon component={IconStatusbarDownsampling} aria-label="Magnification" />{" "}
      <FastTooltip dynamicRenderer={renderMagTooltipContent} placement="top">
        {representativeMag.join("-")}
        {isActiveMagGlobal ? "" : "*"}{" "}
      </FastTooltip>
    </span>
  );
}

function SegmentAndMousePosition() {
  // This component depends on the mouse position which is a fast-changing property.
  // For the sake of performance, it is isolated as a single component.
  const additionalCoordinates = useWkSelector((state) => state.flycam.additionalCoordinates);
  const isPlaneMode = useWkSelector((state) => getIsPlaneMode(state));
  const globalMousePositionRounded = useWkSelector(getGlobalMousePosition);

  return (
    <>
      {isPlaneMode ? <SegmentInfo /> : null}
      {isPlaneMode ? (
        <span className="info-element">
          Pos [
          {globalMousePositionRounded
            ? getPosString(globalMousePositionRounded, additionalCoordinates)
            : "-,-,-"}
          ]
        </span>
      ) : null}
    </>
  );
}

function Statusbar() {
  return (
    <span className="statusbar">
      <BorderToggleButton side="left" inFooter />
      <ShortcutsInfo />
      <Infos />
      <BorderToggleButton side="right" inFooter />
    </span>
  );
}

export default Statusbar;
